import { Button, IconButton } from "@dropbox/dig-components/buttons";
import { createUxaElementId } from "@mirage/analytics/uxa";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import i18n from "@mirage/translations";
import classnames from "classnames";
import { AppFilterDropdown } from "./AppFilterDropdown";
import { ChannelFilterDropdown } from "./ChannelFilterDropdown";
import { ContentTypeFilterDropdown } from "./ContentTypeFilter/ContentTypeFilterDropdown";
import styles from "./FilterBar.module.css";
import { LastUpdatedFilterDropdown } from "./LastUpdatedFilter";
import { PersonFilterDropdown } from "./PersonFilterDropdown";

import { Chip } from "@dropbox/dig-components/chip";
import { UIIcon } from "@dropbox/dig-icons";
import { CommentLine } from "@dropbox/dig-icons/dist/mjs/assets";
import type { DashFilterType } from "@mirage/analytics/events/enums/dash_filter_type";
import { chatConnectors } from "@mirage/mosaics/SearchPage/utils/searchFilterUtils";
import type { PersonObject } from "@mirage/service-people/types";
import type {
  ChannelFilter,
  ChannelForFilter,
  ContentTypeFilter,
  LastUpdatedFilter,
  OCRFilter,
  PersonFilter,
  SearchFilter,
  VerifiedFilter,
} from "@mirage/shared/search/search-filters";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import type { RefObject } from "react";
import type { FilterProps } from "./types";

type Props = {
  connectorFilters?: Array<FilterProps>;
  browserHistoryFilters?: Array<FilterProps>;
  activeFilters?: SearchFilter[];
  disabled?: boolean;
  displayConnectMoreApps?: boolean;
  onLastUpdatedFilterSelect: (filter: LastUpdatedFilter | undefined) => void;
  onContentTypeFilterSelect: (filter: ContentTypeFilter | undefined) => void;
  onPersonFilterSelect: (
    filter: PersonFilter | undefined,
    query: string,
  ) => void;
  onConnectorFilterSelect: (
    connectorId: string,
    connectorLabel: string,
  ) => void;
  onChannelFilterSelect: (filter: ChannelFilter) => void;
  onOcrFilterSelect: () => void;
  onVerifiedFilterSelect: () => void;
  activeUpdatedAtFilter: LastUpdatedFilter | undefined;
  activePersonFilter: PersonFilter | undefined;
  activeContentTypeFilters: ContentTypeFilter[];
  activeChannelFilters: ChannelForFilter[];
  activeOcrFilter?: OCRFilter;
  activeVerifiedFilter?: VerifiedFilter;
  clearFilters?: () => void;
  clearConnectorFilters?: () => void;
  onOpenFilterDropdown: (type: DashFilterType) => void;
  currentAccount?: PersonObject;
  showMissingUrls: boolean;
  onOpenMissingUrls: () => void;
  searchResultsPageRef?: RefObject<HTMLDivElement>;
  shouldShowOcrFilter?: boolean;
  shouldShowVerifiedFilter?: boolean;
};

export default function FilterBar({
  connectorFilters = [],
  browserHistoryFilters = [],
  activeFilters = [],
  onLastUpdatedFilterSelect,
  onContentTypeFilterSelect,
  onPersonFilterSelect,
  onConnectorFilterSelect,
  onChannelFilterSelect,
  onOcrFilterSelect,
  onVerifiedFilterSelect,
  activeUpdatedAtFilter,
  activePersonFilter,
  activeContentTypeFilters,
  activeChannelFilters,
  activeOcrFilter,
  activeVerifiedFilter,
  clearFilters,
  clearConnectorFilters,
  onOpenFilterDropdown,
  currentAccount,
  showMissingUrls,
  onOpenMissingUrls,
  searchResultsPageRef,
  shouldShowOcrFilter,
  shouldShowVerifiedFilter,
}: Props) {
  const anyFilterActive = activeFilters.length > 0;
  const isMobileSize = useIsMobileSize();
  const showClearAll = anyFilterActive;
  const isMessagesActive = activeContentTypeFilters.some((filter) => {
    return filter.id === "messages";
  });
  const isMessagesEnabled = useFeatureFlagValueBool(
    "dash_2025_03_03_search_message_results",
  );
  const isChatAppSelected = activeFilters.some(
    (filter) => !!chatConnectors[filter.id],
  );
  const isOCRFilterEnabled = useFeatureFlagValueBool(
    "dash_2025_07_31_ocr_search_filter",
  );
  const isVerifiedFilterEnabled = useFeatureFlagValueBool(
    "dash_2025_08_04_verified_content_search_filter",
  );
  const shouldShowChannelFilter =
    useFeatureFlagValueBool("dash_2025_03_27_filter_messages_by_channel") &&
    isMessagesEnabled &&
    (isMessagesActive || isChatAppSelected);

  const isOCRFilterVisible = shouldShowOcrFilter && isOCRFilterEnabled;
  const isVerifiedFilterVisible =
    shouldShowVerifiedFilter && isVerifiedFilterEnabled;

  const specialFiltersEnabled =
    isOCRFilterVisible || isVerifiedFilterVisible || shouldShowChannelFilter;

  return (
    <div
      className={classnames(styles.filterBarContainer, {
        [styles.isMobile]: isMobileSize,
      })}
    >
      <div
        className={classnames(styles.filterChipContainer, {
          [styles.isMobile]: isMobileSize,
        })}
      >
        <AppFilterDropdown
          connectorFilters={connectorFilters}
          browserHistoryFilters={browserHistoryFilters}
          onDeselectAll={() => clearConnectorFilters?.()}
          onOpenDropdown={() => onOpenFilterDropdown("connector")}
          onSelect={onConnectorFilterSelect}
        />
        <ContentTypeFilterDropdown
          activeFilters={activeContentTypeFilters}
          onSelect={onContentTypeFilterSelect}
          selected={!!activeContentTypeFilters.length}
          onOpenDropdown={() => onOpenFilterDropdown("file_type")}
        />
        <LastUpdatedFilterDropdown
          onSelect={onLastUpdatedFilterSelect}
          selected={!!activeUpdatedAtFilter}
          activeUpdatedAtFilter={activeUpdatedAtFilter}
          onOpenDropdown={() => onOpenFilterDropdown("last_updated")}
        />
        <PersonFilterDropdown
          onSelect={onPersonFilterSelect}
          selectedPerson={activePersonFilter}
          onOpenDropdown={() => onOpenFilterDropdown("people")}
          currentAccount={currentAccount}
          searchResultsPageRef={searchResultsPageRef}
        />
        {specialFiltersEnabled && (
          <div className={styles.specializedFilterDivider} />
        )}
        {shouldShowChannelFilter && (
          <ChannelFilterDropdown
            onSelect={onChannelFilterSelect}
            selectedChannels={activeChannelFilters}
            onOpenDropdown={() => onOpenFilterDropdown("channel")}
          />
        )}
        {showMissingUrls && (
          <DigTooltip title="Report missing URL (Internal only)">
            <IconButton
              variant="opacity"
              shape="circular"
              aria-label="Report missing URL (Internal only)"
              onClick={onOpenMissingUrls}
            >
              <UIIcon size="small" src={CommentLine} />
            </IconButton>
          </DigTooltip>
        )}
        {isOCRFilterVisible && (
          <Chip
            isSelected={!!activeOcrFilter}
            selectedStyle="stroke"
            onClick={() => onOcrFilterSelect()}
          >
            <Chip.Content>{i18n.t("ocr_filter_text")}</Chip.Content>
          </Chip>
        )}
        {isVerifiedFilterVisible && (
          <Chip
            isSelected={!!activeVerifiedFilter}
            selectedStyle="stroke"
            onClick={() => onVerifiedFilterSelect()}
          >
            <Chip.Content>{i18n.t("verified_filter_text")}</Chip.Content>
          </Chip>
        )}
        {showClearAll && (
          <Button
            className={styles.clearAllButton}
            size="small"
            variant="transparent"
            onClick={clearFilters}
            data-uxa-log={createUxaElementId("clear_filters_button", {
              actionSurfaceComponent: "filter_chip",
              featureLine: "search",
            })}
          >
            {i18n.t("clear_filters")}
          </Button>
        )}
      </div>
    </div>
  );
}
